import { useList, useUpdate } from '@refinedev/core';
import { useState } from 'react';

import { ui } from '../ui';

/**
 * Configuração remota (história 21).
 *
 * Preço, episódios grátis e as chaves liga/desliga vivem em `app_config`, nunca fixos no
 * código (regra 10 do CLAUDE.md). Esta tela é o único lugar onde eles mudam — e muda em
 * produção, na hora, sem publicar nada.
 *
 * **As chaves perigosas ficam em cima e com aviso.** `paywall_enabled` é a mais séria: ela
 * ergue muro para o público inteiro. Tem interruptor próprio, separado de `ads_enabled`, de
 * propósito — ligar anúncio não pode levantar parede junto (regra 10). O texto ao lado de
 * cada uma diz o que acontece ao ligar, porque "paywall_enabled: false" não explica nada a
 * quem está decidindo às onze da noite.
 *
 * Só o admin chega aqui: a política de `app_config` exige `is_admin()` para escrever, e a
 * leitura pública só enxerga as chaves marcadas `is_public`.
 */
type Linha = { key: string; value: unknown; is_public: boolean };

/** O que cada chave significa para quem decide — não para quem programou. */
const EXPLICACAO: Record<string, { titulo: string; efeito: string; perigosa?: boolean }> = {
  paywall_enabled: {
    titulo: 'Parede de pagamento',
    efeito:
      'Ligada, os episódios depois do limite grátis passam a exigir passe ou anúncio. Desligada, o catálogo inteiro é livre.',
    perigosa: true,
  },
  ads_enabled: {
    titulo: 'Anúncios de terceiros',
    efeito:
      'Ligado, o intervalo mostra anúncio do Ad Manager. Desligado, mostra trailer nosso. Não levanta a parede sozinho.',
    perigosa: true,
  },
  rewarded_enabled: {
    titulo: 'Liberar com anúncio',
    efeito: 'Deixa a pessoa liberar um episódio assistindo a um anúncio, dentro do limite do dia.',
  },
  recurring_enabled: {
    titulo: 'Assinatura recorrente',
    efeito: 'Pix Automático. Fica desligado até a V1.1.',
    perigosa: true,
  },
  free_episodes: {
    titulo: 'Episódios grátis',
    efeito: 'Quantos episódios abrem sem passe quando a parede está ligada.',
  },
  interstitial_every: {
    titulo: 'Intervalo a cada',
    efeito: 'De quantos em quantos episódios aparece um intervalo na maratona.',
  },
  feed_ad_every: {
    titulo: 'Intervalo no feed a cada',
    efeito: 'De quantas em quantas séries aparece um intervalo no feed "Para você".',
  },
  reward_daily_limit: {
    titulo: 'Liberações por dia',
    efeito: 'Quantos episódios cada pessoa pode liberar com anúncio por dia.',
  },
};

export function Configuracao() {
  const { result } = useList<Linha>({
    resource: 'app_config',
    pagination: { pageSize: 100 },
    sorters: [{ field: 'key', order: 'asc' }],
  });

  const { mutate } = useUpdate();
  const [salvando, setSalvando] = useState<string | null>(null);

  const linhas = result?.data ?? [];

  function guardar(chave: string, valor: unknown) {
    setSalvando(chave);
    mutate(
      {
        resource: 'app_config',
        id: chave,
        values: { value: valor },
        // A chave primária de `app_config` é `key`, não `id`. Sem isto o provedor do refine
        // monta `?id=eq.paywall_enabled`, a coluna não existe, e a tela volta sozinha ao
        // valor antigo — falhando em silêncio, que é o pior jeito de falhar numa tela que
        // liga e desliga parede de pagamento.
        meta: { idColumnName: 'key' },
      },
      { onSettled: () => setSalvando(null) },
    );
  }

  // As explicadas primeiro, na ordem em que importam; o resto segue em ordem alfabética.
  const ordem = Object.keys(EXPLICACAO);
  const ordenadas = [...linhas].sort((a, b) => {
    const ia = ordem.indexOf(a.key);
    const ib = ordem.indexOf(b.key);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return a.key.localeCompare(b.key);
  });

  return (
    <div>
      <h1 style={ui.h1}>Configuração</h1>
      <p style={ui.dim}>
        Vale na hora, para todo mundo, sem publicar nada. Mudanças aqui aparecem no site na próxima
        vez que cada pessoa abrir o aplicativo.
      </p>

      <div style={{ display: 'grid', gap: 12, marginTop: 24 }}>
        {ordenadas.map((linha) => {
          const explicacao = EXPLICACAO[linha.key];
          const booleana = typeof linha.value === 'boolean';
          const numerica = typeof linha.value === 'number';

          return (
            <div
              key={linha.key}
              style={{
                ...ui.grupo,
                borderColor: explicacao?.perigosa ? '#f08a24' : undefined,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <strong>{explicacao?.titulo ?? linha.key}</strong>
                  <div style={ui.dim}>{explicacao?.efeito ?? 'Chave interna.'}</div>
                  <code style={{ ...ui.dim, fontSize: 11 }}>{linha.key}</code>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {booleana && (
                    <button
                      type="button"
                      onClick={() => guardar(linha.key, !linha.value)}
                      disabled={salvando === linha.key}
                      style={linha.value ? ui.botao : ui.botaoSecundario}
                    >
                      {salvando === linha.key ? 'Salvando…' : linha.value ? 'Ligado' : 'Desligado'}
                    </button>
                  )}

                  {numerica && (
                    <input
                      type="number"
                      defaultValue={String(linha.value)}
                      // `onBlur`, não `onChange`: salvar a cada tecla gravaria "1" no caminho
                      // para "15" — e por um instante o limite do dia seria 1 para todo mundo.
                      onBlur={(e) => {
                        const novo = Number(e.target.value);
                        if (Number.isFinite(novo) && novo !== linha.value) {
                          guardar(linha.key, novo);
                        }
                      }}
                      style={{ ...ui.campo, width: 96 }}
                    />
                  )}

                  {!booleana && !numerica && (
                    <code style={ui.dim}>{JSON.stringify(linha.value)}</code>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
